import React, { useContext, useEffect, useState } from "react";
import InfoLineView, { InfoLine } from "../../Components/InfoLineView";
import { TabPane, Tabs } from "../../Components/Tabs";
import {
  CharacterDTO,
  ItemDTO,
  PlayerNameHistory,
  PropertyDTO,
  VehicleDTO,
  Wealth,
} from "../../Networking/Interface/Account/AccountInterface";
import { UserContext } from "../../User/UserContext";
import { JobClass, UserRole } from "../../Utils/enums";
import ItemsView from "../Assets/ItemsView";
import VehicleView from "../Assets/VehicleView";
import WealthView from "../Assets/WealthView";
import PlaytimeGraph from "./PlaytimeGraph";
import { SunsetAPIAccountContext } from "../../Networking/Interface/Account/AccountAPIProvider";
import { JobDataDTO } from "../../Networking/SunsetAPI";
import PropertyView from "../Assets/PropertyView";
import { CharacterPropertyMap } from "../../Components/PropertyMap/PropertyMap";
import SetCharacterNameButton from "../../Staff/Actions/SetCharacterName";
import {
  MdAccessTime,
  MdWork,
  MdCalendarToday,
  MdFavorite,
  MdShield,
  MdRestaurant,
  MdLocalDrink,
  MdGavel,
  MdPersonOff,
  MdLink,
  MdLinkOff,
  MdLocationOn,
} from "react-icons/md";
import statStyles from "./CharacterStats.module.css";

interface CharacterViewProps {
  character: CharacterDTO;
}

export const CharacterView: React.FC<CharacterViewProps> = ({ character }) => {
  const api = useContext(SunsetAPIAccountContext);
  const [jobData, setJobData] = useState<JobDataDTO | null>(null);
  const [vehicles, setVehicles] = useState<VehicleDTO[]>([]);
  const [properties, setProperties] = useState<PropertyDTO[]>([]);
  const [wealth, setWealth] = useState<Wealth | undefined>();
  const [items, setItems] = useState<ItemDTO[]>([]);
  const [nameHistory, setNameHistory] = useState<PlayerNameHistory[]>([]);
  const { user, isLoading } = useContext(UserContext);

  const fetchData = async () => {
    if (api) {
      let response = await api.getCharacterJobData(
        character.id,
        character.characterName
      );
      if (response?.success) {
        setJobData(response.data!);
      }
      if (hasFullView()) {
        let vehicleResponse = await api.getVehicles(character.characterName);
        if (vehicleResponse.success) {
          setVehicles(vehicleResponse.data!);
        }
        let propertyResponse = await api.getProperties(character.characterName);
        if (propertyResponse.success) {
          setProperties(propertyResponse.data!);
        }
        let wealthResponse = await api.getWealth(character.characterName);
        if (wealthResponse.success) {
          setWealth(wealthResponse.data!);
        }
        let itemResponse = await api.getItems(character.characterName);
        if (itemResponse.success) {
          setItems(itemResponse.data!);
        }
        let nameHistoryResponse = await api.getPlayerNameHistory(character.id);
        if (nameHistoryResponse.success) {
          if (nameHistoryResponse.data?.length === 0) {
            return;
          }
          setNameHistory(nameHistoryResponse.data!);
        }
      }
    }
  };

  useEffect(() => {
    fetchData();
  }, [character, api]);

  const hasFullView = (): boolean => {
    if (!user || isLoading) {
      return false;
    }
    if (Number.parseInt(user.id) === character.userId) {
      return true;
    }
    if (user.role >= UserRole.Admin) {
      return true;
    }
    return false;
  };
  const hasRole = (minLevel: number) => {
    return user && user.role >= minLevel;
  };
  const formatPlaytime = (totalMinutes: number): string => {
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const mins = Math.floor(totalMinutes % 60);
    if (days > 0) return `${days}d ${hours}h ${mins}m`;
    if (hours > 0) return `${hours}h ${mins}m`;
    return `${mins}m`;
  };

  const formatCreationDate = (timestamp: number): string => {
    if (!timestamp) return "Unknown";
    const date = new Date(timestamp * 1000);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    const dateStr = date.toLocaleDateString();
    if (diffDays > 365) {
      const years = Math.floor(diffDays / 365);
      return `${dateStr} (${years}y ago)`;
    }
    return `${dateStr} (${diffDays}d ago)`;
  };

  const progressColor = (pct: number): string => {
    if (pct > 60) return "#27ae60";
    if (pct > 30) return "#f39c12";
    return "#e74c3c";
  };

  return (
    <Tabs>
      <TabPane tabKey="1" tabTitle="Stats">
        <div className={statStyles.statsHeader}>
          <h3 className={statStyles.sectionLabel}>Character Overview</h3>
          {hasRole(6) && (
            <SetCharacterNameButton userId={character.id} onSuccess={fetchData} />
          )}
        </div>

        {/* Stat pills */}
        <div className={statStyles.pillRow}>
          <div className={statStyles.pill}>
            <MdAccessTime className={statStyles.pillIcon} />
            <div className={statStyles.pillContent}>
              <span className={statStyles.pillLabel}>Playtime</span>
              <span className={statStyles.pillValue}>{formatPlaytime(character.totalPlaytime)}</span>
            </div>
          </div>
          {jobData && (
            <div className={statStyles.pill}>
              <MdWork className={statStyles.pillIcon} />
              <div className={statStyles.pillContent}>
                <span className={statStyles.pillLabel}>Occupation</span>
                <span className={statStyles.pillValue}>{JobClass[jobData.jobClass]}</span>
              </div>
            </div>
          )}
          <div className={statStyles.pill}>
            <MdCalendarToday className={statStyles.pillIcon} />
            <div className={statStyles.pillContent}>
              <span className={statStyles.pillLabel}>Created</span>
              <span className={statStyles.pillValue}>{formatCreationDate(character.creationDate)}</span>
            </div>
          </div>
        </div>

        {/* Admin details */}
        {hasRole(3) && character.adminDetails && (
          <div className={statStyles.adminSection}>
            <h3 className={statStyles.sectionLabel}>Admin Details</h3>
            <div className={statStyles.pillRow}>
              {[
                { label: "Health", value: character.adminDetails.health, max: 100, invert: false, icon: <MdFavorite className={statStyles.pillIcon} /> },
                { label: "Armour", value: character.adminDetails.armour, max: 100, invert: false, icon: <MdShield className={statStyles.pillIcon} /> },
                { label: "Hunger", value: character.adminDetails.hunger, max: 50, invert: true, icon: <MdRestaurant className={statStyles.pillIcon} /> },
                { label: "Thirst", value: character.adminDetails.thirst, max: 50, invert: true, icon: <MdLocalDrink className={statStyles.pillIcon} /> },
              ].map((stat) => {
                const rawPct = Math.min(100, Math.max(0, (stat.value / stat.max) * 100));
                const displayPct = stat.invert ? 100 - rawPct : rawPct;
                const colorPct = stat.invert ? 100 - rawPct : rawPct;
                return (
                  <div key={stat.label} className={statStyles.progressPill}>
                    <div className={statStyles.progressHeader}>
                      <span className={statStyles.progressLabel}>{stat.label}</span>
                      <span className={statStyles.progressValue}>{Math.round(stat.value)}/{stat.max}</span>
                    </div>
                    <div className={statStyles.progressTrack}>
                      <div
                        className={statStyles.progressBar}
                        style={{ width: `${displayPct}%`, backgroundColor: progressColor(colorPct) }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Status badges */}
            <div className={statStyles.statusRow}>
              {!!character.adminDetails.jailTime && (
                <span className={`${statStyles.statusBadge} ${statStyles.statusActive}`}>
                  <MdGavel size={14} /> Jailed ({character.adminDetails.jailTime}s)
                </span>
              )}
              {!!character.adminDetails.isKnocked && (
                <span className={`${statStyles.statusBadge} ${statStyles.statusActive}`}>
                  <MdPersonOff size={14} /> Knocked
                </span>
              )}
              {!!character.adminDetails.isCuffed && (
                <span className={`${statStyles.statusBadge} ${statStyles.statusActive}`}>
                  <MdLink size={14} /> Cuffed
                </span>
              )}
              {!!character.adminDetails.isTied && (
                <span className={`${statStyles.statusBadge} ${statStyles.statusActive}`}>
                  <MdLinkOff size={14} /> Tied
                </span>
              )}
              {!character.adminDetails.jailTime && !character.adminDetails.isKnocked &&
               !character.adminDetails.isCuffed && !character.adminDetails.isTied && (
                <span className={`${statStyles.statusBadge} ${statStyles.statusInactive}`}>
                  No active statuses
                </span>
              )}
              <span className={`${statStyles.statusBadge} ${statStyles.statusLocation}`}>
                <MdLocationOn size={14} />
                {character.adminDetails.lastX.toFixed(2)}, {character.adminDetails.lastY.toFixed(2)}, {character.adminDetails.lastZ.toFixed(2)}
              </span>
            </div>
          </div>
        )}

        {/* Playtime chart */}
        <div className={statStyles.chartWrapper}>
          <p className={statStyles.chartLabel}>Playtime — Last 30 Days</p>
          <PlaytimeGraph character={character} />
        </div>
      </TabPane>
      {hasFullView() && vehicles?.length > 0 && (
        <TabPane tabKey="2" tabTitle="Vehicles">
          <VehicleView vehicles={vehicles}></VehicleView>
        </TabPane>
      )}
      {hasFullView() && !!wealth && (
        <TabPane tabKey="3" tabTitle="Assets">
          <WealthView
            userId={character.id}
            wealth={wealth}
            characterName={character.characterName} // Pass character name
            setWealth={setWealth} // Pass setWealth function
          />
          <ItemsView items={items}></ItemsView>
        </TabPane>
      )}
      {hasFullView() && !!wealth && (
        <TabPane
          style={{ marginBottom: "30px" }}
          tabKey="4"
          tabTitle="Name History"
        >
          {nameHistory.length === 0 ? (
            <p style={{ textAlign: "center", color: "#888", padding: "20px 0" }}>No name changes recorded.</p>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "12px", padding: "8px 0" }}>
              {nameHistory.map((entry, index) => (
                <div
                  key={index}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    padding: "14px 18px",
                    borderRadius: "8px",
                    background: "var(--gray-trans-color)",
                    borderLeft: "3px solid var(--orange-color)",
                  }}
                >
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "8px", flexWrap: "wrap" }}>
                      <span style={{ fontWeight: 600, color: "#111", fontSize: "0.9rem" }}>{entry.oldname}</span>
                      <span style={{ color: "var(--orange-color)", fontSize: "0.85rem" }}>→</span>
                      <span style={{ fontWeight: 600, color: "var(--orange-color)", fontSize: "0.9rem" }}>{entry.newname}</span>
                    </div>
                    {entry.reason && (
                      <div style={{ fontSize: "0.78rem", color: "#555", marginTop: "4px" }}>
                        {entry.reason}
                      </div>
                    )}
                  </div>
                  <div style={{ fontSize: "0.75rem", color: "#888", whiteSpace: "nowrap", flexShrink: 0 }}>
                    {new Date(entry.date).toLocaleDateString()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabPane>
      )}
      {hasFullView() && properties?.length > 0 && (
        <TabPane tabKey="5" tabTitle="Properties">
          <PropertyView properties={properties}></PropertyView>
          {/* <div style={{ marginTop: "30px" }}>
            <h3 style={{ color: "#525252", marginBottom: "16px" }}>
              Map Overview
            </h3>
            <CharacterPropertyMap
              character={character}
              properties={properties}
              height="520px"
            />
          </div> */}
        </TabPane>
      )}
    </Tabs>
  );
};
